import { useEffect, useRef, useState } from "react";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useGoogleTranslateRefresh } from "@/hooks/useGoogleTranslateRefresh";
import type { PlaceResult } from "@/hooks/useGooglePlacesAutocomplete";
import {
  extractAptUnit,
  parseAddressComponents,
} from "@/lib/utils/addressParser";
import { formatAddressDisplay } from "@/features/applicant/utils/addressHelpers";
import { CircleAlert } from "lucide-react";
import styles from "../../../styles/applicationDetail.module.css";
import editStyles from "../../../styles/editForm.module.css";
import * as angErr from "../../../utils/aboutYourselfAngularInlineMessages";
import {
  isUsCountry,
  US_STATE_OPTIONS,
  ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT,
} from "../aboutYourselfTabHelpers";
import { withInputError } from "./aboutYourselfEditFieldHelpers";
import type { AlternateAddressEditFormProps } from "../../../types/tabs";
import type { AlternateAddressLike } from "../../../types/utils";
import type { AboutYourselfAlternateAddressFormData } from "../../../types/tabs";

function AngularShowControlError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className={editStyles.showControlError} role="alert">
      <CircleAlert className={editStyles.showControlErrorIcon} aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function AlternateAddressEditForm({
  addressData,
  alternateIndex,
  onChange,
  exposeInlineValidation = false,
}: AlternateAddressEditFormProps) {
  const altTid = (slug: string, suffix: string) =>
    `application-about-tab-alternate-address-${alternateIndex}-${slug}-${suffix}`;
  const formatAddressSearchValue = (
    addr: AlternateAddressLike | AboutYourselfAlternateAddressFormData | null | undefined,
  ): string => {
    const hasAddressParts = [
      addr?.house_no,
      addr?.street,
      addr?.street_type,
      addr?.apartment,
      addr?.city,
      addr?.state,
      addr?.zip_code,
    ].some((value) => String(value ?? "").trim().length > 0);
    return hasAddressParts ? formatAddressDisplay(addr ?? {}) : "";
  };
  const [formData, setFormData] = useState<AboutYourselfAlternateAddressFormData>({
    ra_id: addressData?.ra_id || "",
    street_address: formatAddressSearchValue(addressData),
    house_no: addressData?.house_no || "",
    street: addressData?.street || "",
    apartment: addressData?.apartment || "",
    city: addressData?.city || "",
    state: addressData?.state || "",
    zip_code: addressData?.zip_code || "",
    country: addressData?.country || "US",
    outside_us:
      Boolean(addressData?.outside_us) ||
      !isUsCountry(addressData?.country || "US"),
  });
  const [addressFieldsDirty, setAddressFieldsDirty] = useState<
    Record<string, boolean>
  >({});
  // Alternate fields open by default (Angular); Close/View Detail is collapse only.
  const [showDetail, setShowDetail] = useState(true);
  const resetIdentity = `alternate:${alternateIndex}:${addressData?.ra_id || "__new__"}`;
  const lastResetIdentityRef = useRef(resetIdentity);

  // Angular keeps these fields in the DOM (d-none) so Google Translate scans them once.
  // React mounts them on demand, so re-run translateHtml when this row/its detail appears.
  useGoogleTranslateRefresh(showDetail);

  useEffect(() => {
    if (!exposeInlineValidation) return;
    if (angErr.alternateAddressHasInlineErrors(formData)) {
      setShowDetail(true);
    }
  }, [exposeInlineValidation, formData]);

  useEffect(() => {
    const revealDetailForErrors = () => {
      if (angErr.alternateAddressHasInlineErrors(formData)) {
        setShowDetail(true);
      }
    };
    window.addEventListener(
      ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT,
      revealDetailForErrors,
    );
    return () => {
      window.removeEventListener(
        ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT,
        revealDetailForErrors,
      );
    };
  }, [formData]);

  useEffect(() => {
    if (!addressData || resetIdentity === lastResetIdentityRef.current) return;
    lastResetIdentityRef.current = resetIdentity;
    setFormData({
      ra_id: addressData.ra_id || "",
      street_address: formatAddressSearchValue(addressData),
      house_no: addressData.house_no || "",
      street: addressData.street || "",
      apartment: addressData.apartment || "",
      city: addressData.city || "",
      state: addressData.state || "",
      zip_code: addressData.zip_code || "",
      country: addressData.country || "US",
      outside_us:
        Boolean(addressData.outside_us) ||
        !isUsCountry(addressData.country || "US"),
    });
    setAddressFieldsDirty({});
    if (addressData.house_no || addressData.street || addressData.city) {
      setShowDetail(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetIdentity]);

  const outsideUs = Boolean(formData.outside_us) || !isUsCountry(formData.country);
  const rowStarted = angErr.alternateRowHasAnyField(formData);
  const showField = (key: string) =>
    (exposeInlineValidation && rowStarted) || addressFieldsDirty[key];
  const altFieldErr = (key: string, resolver: () => string | null) =>
    showField(key) ? resolver() : null;

  const houseNoErr = altFieldErr("house_no", () =>
    angErr.angularAlternateStreetNoError(formData.house_no),
  );
  const streetErr = altFieldErr("street", () =>
    angErr.angularStreetNameError(formData.street),
  );
  const cityErr = altFieldErr("city", () => angErr.angularCityError(formData.city));
  const stateErr = altFieldErr("state", () => angErr.angularStateError(formData.state));
  const zipErr = altFieldErr("zip_code", () =>
    angErr.angularAlternateZipError(formData.zip_code, outsideUs),
  );
  const countryErr = altFieldErr("country", () =>
    angErr.angularCountryError(formData.country),
  );

  const handleChange = (field: string, value: string | boolean) => {
    setAddressFieldsDirty((dirty) => ({ ...dirty, [field]: true }));
    const newFormData = { ...formData, [field]: value };
    if (field === "country") {
      newFormData.outside_us = !isUsCountry(String(value));
    }
    setFormData(newFormData);
    onChange(newFormData);
  };

  return (
    <div>
      {/* Street Address Input with View Detail Button */}
      <div className={editStyles.formRow}>
        <div className={editStyles.formGroup} style={{ flex: 2 }}>
          <label className={editStyles.label} data-testid={altTid("address", "label")}>Address</label>
          <div data-testid={altTid("address", "input")}><AddressAutocomplete
            value={formData.street_address ?? ""}
            onChange={(value) => handleChange("street_address", value)}
            onPlaceSelect={(place: PlaceResult) => {
              const parsed = parseAddressComponents(place);
              const formattedAddress = parsed.formattedAddress || "";
              const aptUnit = extractAptUnit(formattedAddress);
              const country = parsed.country || formData.country || "US";

              const next = {
                ...formData,
                street_address: formattedAddress,
                house_no: parsed.streetNumber || "",
                street: parsed.streetName || "",
                apartment: aptUnit || formData.apartment || "",
                city: parsed.city || "",
                state: parsed.state || "",
                zip_code: parsed.zipCode || "",
                country,
                outside_us: !isUsCountry(country),
              };

              setFormData(next as AboutYourselfAlternateAddressFormData);
              onChange(next as AboutYourselfAlternateAddressFormData);
              setAddressFieldsDirty((dirty) => ({
                ...dirty,
                street_address: true,
                house_no: true,
                street: true,
                apartment: true,
                city: true,
                state: true,
                zip_code: true,
                country: true,
              }));
              setShowDetail(true);
            }}
            placeholder="Enter your address"
            label=""
          />
          </div>
        </div>
        <div
          className={`${editStyles.formGroup} ${styles.aboutAlternateDetailToggleGroup}`}
          style={{ flex: 0, alignSelf: "flex-end" }}
        >
          <button
            type="button"
            className={`${styles.btnSmall} ${styles.aboutAlternateDetailToggle}`}
            data-testid={altTid("toggle-detail", "button")}
                onClick={() => setShowDetail(!showDetail)}
            style={{ whiteSpace: "nowrap" }}
          >
            {showDetail ? "Close Detail" : "View Detail"}
          </button>
        </div>
      </div>

      {/* Detailed Address Fields (shown when View Detail is clicked) */}
      {showDetail && (
        <>
          <div className={editStyles.formRow}>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid={altTid("street-no", "label")}>
                Street No.<span className={editStyles.required}>*</span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(houseNoErr))}
                value={formData.house_no}
                data-testid={altTid("street-no", "input")}
                onChange={(e) => handleChange("house_no", e.target.value)}
                placeholder="Street No."
              />
              <AngularShowControlError message={houseNoErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid={altTid("street-name", "label")}>
                Street Name<span className={editStyles.required}>*</span> (ex.
                Main)
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(streetErr))}
                value={formData.street}
                data-testid={altTid("street-name", "input")}
                onChange={(e) => handleChange("street", e.target.value)}
                placeholder="Street"
              />
              <AngularShowControlError message={streetErr} />
            </div>
          </div>

          <div className={editStyles.formRow}>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid={altTid("city", "label")}>
                City<span className={editStyles.required}>*</span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(cityErr))}
                value={formData.city}
                data-testid={altTid("city", "input")}
                onChange={(e) => handleChange("city", e.target.value)}
                placeholder="City"
              />
              <AngularShowControlError message={cityErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid={altTid("state", "label")}>
                State<span className={editStyles.required}>*</span>
              </label>
              {!isUsCountry(formData.country) ? (
                <input
                  type="text"
                  className={withInputError(editStyles.input, Boolean(stateErr))}
                  value={formData.state}
                  data-testid={altTid("state", "input")}
                onChange={(e) => handleChange("state", e.target.value)}
                  placeholder="State"
                />
              ) : (
                <SearchableSelect
                  triggerDataTestId={altTid("state", "select")}
                  options={US_STATE_OPTIONS.map((s) => ({
                    value: s.code,
                    label: s.name,
                  }))}
                  value={formData.state || ""}
                  onChange={(value) => handleChange("state", String(value))}
                  placeholder="Select State"
                  searchPlaceholder="Search State"
                  searchable={false}
                  className={`${styles.aboutStateCustomSelect}${stateErr ? ` ${editStyles.inputError}` : ""}`}
                />
              )}
              <AngularShowControlError message={stateErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid={altTid("zipcode", "label")}>
                Zipcode<span className={editStyles.required}>*</span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(zipErr))}
                value={formData.zip_code}
                data-testid={altTid("zipcode", "input")}
                onChange={(e) => handleChange("zip_code", e.target.value)}
                placeholder="Zipcode"
              />
              <AngularShowControlError message={zipErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid={altTid("country", "label")}>
                Country<span className={editStyles.required}>*</span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(countryErr))}
                value={formData.country}
                data-testid={altTid("country", "input")}
                onChange={(e) => handleChange("country", e.target.value)}
                placeholder="Country"
              />
              <AngularShowControlError message={countryErr} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
